import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, Surface, Text, useTheme } from 'react-native-paper';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { apiClient } from '../src/api/client';
import { API_BASE } from '../src/api/config';

type VerificationStatus = 'checking-auth' | 'loading' | 'success' | 'error';

interface BotConfirmResponse {
  status?: string;
  detail?: string;
}

const BOT_CONFIRM_URL = `${API_BASE}/api/support/bot/associate/confirm/`;

const normalizeParam = (value: string | string[] | undefined): string => {
  if (Array.isArray(value)) {
    return value[0] || '';
  }
  return value || '';
};

const getErrorMessage = (error: unknown): string => {
  if (!error) {
    return 'Não foi possível confirmar o código. Tenta novamente.';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error === 'object' && error !== null) {
    const errorData = error as { detail?: unknown; code?: unknown; non_field_errors?: unknown };
    if (typeof errorData.detail === 'string') {
      return errorData.detail;
    }
    if (Array.isArray(errorData.code) && typeof errorData.code[0] === 'string') {
      return errorData.code[0];
    }
    if (Array.isArray(errorData.non_field_errors) && typeof errorData.non_field_errors[0] === 'string') {
      return errorData.non_field_errors[0];
    }
  }

  return 'Não foi possível confirmar o código. Tenta novamente.';
};

export default function BotVerifyScreen() {
  const theme = useTheme();
  const router = useRouter();
  const params = useLocalSearchParams<{ code?: string | string[] }>();
  const code = useMemo(() => normalizeParam(params.code).trim(), [params.code]);

  const [status, setStatus] = useState<VerificationStatus>('checking-auth');
  const [error, setError] = useState<string | null>(null);
  const [retryDisabled, setRetryDisabled] = useState(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialConfirmCodeRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const scheduleDashboardRedirect = useCallback(() => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
    }

    redirectTimerRef.current = setTimeout(() => {
      router.replace('/dashboard/home');
    }, 3000);
  }, [router]);

  const confirmCode = useCallback(async () => {
    if (!code) {
      setStatus('error');
      setError('O link de verificação não inclui um código válido.');
      return;
    }

    setStatus('loading');
    setError(null);

    const token = await AsyncStorage.getItem('userToken');
    const response = await apiClient<BotConfirmResponse>(BOT_CONFIRM_URL, {
      method: 'POST',
      headers: token ? { Authorization: `Token ${token}` } : undefined,
      body: JSON.stringify({ code }),
    });

    if (response.ok) {
      setStatus('success');
      scheduleDashboardRedirect();
      return;
    }

    setStatus('error');
    setError(getErrorMessage(response.error));
  }, [code, scheduleDashboardRedirect]);

  useEffect(() => {
    if (initialConfirmCodeRef.current === code) {
      return;
    }
    initialConfirmCodeRef.current = code;

    const verifyAuthAndConfirm = async () => {
      const token = await AsyncStorage.getItem('userToken');
      if (!token) {
        // Save code so we can return here after login
        await AsyncStorage.setItem('pendingBotVerifyCode', code);
        router.replace('/');
        return;
      }

      // Clear any stale pending code
      await AsyncStorage.removeItem('pendingBotVerifyCode');
      await confirmCode();
    };

    verifyAuthAndConfirm();
  }, [confirmCode, router]);

  const retry = useCallback(() => {
    setRetryDisabled(true);
    void confirmCode();
    setTimeout(() => setRetryDisabled(false), 3000);
  }, [confirmCode]);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.center}>
        <Surface style={styles.content} elevation={1}>
          {status === 'checking-auth' || status === 'loading' ? (
            <>
              <ActivityIndicator size="large" color={theme.colors.primary} />
              <Text variant="headlineSmall" style={styles.title}>
                A confirmar ligação ao WhatsApp
              </Text>
              <Text variant="bodyMedium" style={styles.description}>
                Estamos a validar o código enviado pelo bot de suporte.
              </Text>
            </>
          ) : null}

          {status === 'success' ? (
            <>
              <Text variant="headlineSmall" style={[styles.title, { color: theme.colors.primary }]}>
                Número verificado
              </Text>
              <Text variant="bodyMedium" style={styles.description}>
                A tua conta ficou associada ao WhatsApp. Vais voltar ao dashboard dentro de instantes.
              </Text>
              <Button mode="contained" onPress={() => router.replace('/dashboard/home')} style={styles.button}>
                Ir para o dashboard
              </Button>
            </>
          ) : null}

          {status === 'error' ? (
            <>
              <Text variant="headlineSmall" style={[styles.title, { color: theme.colors.error }]}>
                Não foi possível verificar
              </Text>
              <Text variant="bodyMedium" style={styles.description}>
                {error}
              </Text>
              <Button mode="contained" onPress={retry} disabled={retryDisabled} style={styles.button}>
                {retryDisabled ? 'Aguarde...' : 'Tentar novamente'}
              </Button>
              <Button mode="text" onPress={() => router.replace('/dashboard/home')}>
                Voltar ao dashboard
              </Button>
            </>
          ) : null}
        </Surface>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    padding: 16,
  },
  content: {
    borderRadius: 8,
    padding: 20,
  },
  title: {
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  description: {
    marginBottom: 14,
    opacity: 0.8,
    textAlign: 'center',
  },
  button: {
    marginTop: 6,
  },
});
